'use client';

import { useCallback, useState } from 'react';

export type AssetUploadRequest = {
  asset_kind: string;
  filename: string;
  content_type?: string;
};

export type AssetUploadPayload = {
  asset_key: string;
  upload_url: string;
};

type UseAssetUploadOptions = {
  requestUpload: (payload: AssetUploadRequest) => Promise<AssetUploadPayload>;
  uploadMethod?: 'PUT' | 'POST';
};

type UploadAssetArgs = {
  file: File;
  assetKind: string;
};

export function useAssetUpload({
  requestUpload,
  uploadMethod = 'PUT',
}: UseAssetUploadOptions) {
  const [isUploading, setIsUploading] = useState(false);

  const uploadAsset = useCallback(
    async ({ file, assetKind }: UploadAssetArgs) => {
      setIsUploading(true);

      try {
        const payload = await requestUpload({
          asset_kind: assetKind,
          filename: file.name,
          content_type: file.type || undefined,
        });

        const response = await fetch(payload.upload_url, {
          method: uploadMethod,
          body: file,
          headers: file.type
            ? {
                'Content-Type': file.type,
              }
            : undefined,
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Upload failed.');
        }

        return payload.asset_key;
      } finally {
        setIsUploading(false);
      }
    },
    [requestUpload, uploadMethod],
  );

  return {
    uploadAsset,
    isUploading,
  };
}
